/**
 * Copies of a result, for the usage counter (lib/usage.ts). People often copy a result instead
 * of downloading it, so a copy is an outcome the stats page counts beside a download.
 *
 * TWO WAYS TO COPY.
 *
 *   * A Copy button (`countCopy`): every press that put the text on the clipboard counts, as
 *     every download click does. Sent as `copied` with the format `button`.
 *   * Text selected in a result and copied with Ctrl/Cmd+C, the context menu or the Edit menu
 *     (`watchCopies`): the `copy` event on the result's region. People press Ctrl+C again and
 *     again, so a region counts ONCE per result per page view: once on a transcript, and on a
 *     tool page once more after each new file loaded (usage.ts `loadsOf`). Sent as `copied` with
 *     the format `selection`. A copy with nothing selected copies nothing and counts nothing.
 *
 * Nothing about the text goes anywhere: the listener never reads the clipboard or the
 * selection's words, only whether there is a selection. It never cancels or changes the copy.
 *
 * NOT WATCHED: the subtitle editor's rows (/subtitle-editor, the auto subtitle generator).
 * Copying there is mostly moving words between cues while editing, not taking the result away.
 * The text box on /txt-to-pdf and /text-to-pdf is the visitor's own input, and their result is
 * the PDF. The API page's snippets are not results.
 */

import { countUse, loadsOf } from './usage.ts';

/** A Copy button put `tool`'s result on the clipboard. Every press counts. */
export function countCopy(tool: string): void {
  countUse('copied', tool, 'button');
}

interface TextBox {
  readonly selectionStart?: unknown;
  readonly selectionEnd?: unknown;
}

/**
 * Whether a copy aimed at `target` carries text: a selection inside a text box, or a selection
 * on the page that is not empty. Where neither can be read, the copy event is taken at its word.
 */
export function copiesText(target: EventTarget | null): boolean {
  const box = target as TextBox | null;
  if (box && typeof box.selectionStart === 'number' && typeof box.selectionEnd === 'number') {
    return box.selectionEnd > box.selectionStart;
  }
  const selection = typeof getSelection === 'function' ? getSelection() : null;
  if (selection === null) return true;
  return !selection.isCollapsed && selection.toString().trim() !== '';
}

/**
 * Count text selected in any of `regions` and copied, at most once per result: the regions are
 * one result's views (a tool page's table and its diff), so they share the once. `count` sends
 * it, a `copied` under `tool` with the format `selection` unless the caller sends its own (the
 * microphone panel's `live_copy`). Missing regions are skipped; nothing here throws.
 */
export function watchCopies(
  regions: readonly (EventTarget | null | undefined)[],
  tool: string,
  count: () => void = () => countUse('copied', tool, 'selection'),
): void {
  /** `loadsOf(tool)` when this result's copy was counted, or null before one was. */
  let countedAt: number | null = null;
  const onCopy = (ev: Event): void => {
    try {
      if (!copiesText(ev.target)) return;
      const result = loadsOf(tool);
      if (countedAt === result) return;
      countedAt = result;
      count();
    } catch {
      /* counting never surfaces */
    }
  };
  for (const region of regions) {
    try {
      if (region && typeof region.addEventListener === 'function') {
        region.addEventListener('copy', onCopy);
      }
    } catch {
      /* counting never surfaces */
    }
  }
}
